import { useState } from "react";
import { Table, Space, Row, Col, Button, Tooltip, Popconfirm, Tag } from "antd";
import { FaTrash, FaPhoneAlt } from "react-icons/fa";
import { FaRegEye, FaRegPenToSquare, FaClockRotateLeft, FaArrowsRotate } from "react-icons/fa6";
import "./ParticipantesTable.css";

export function ParticipantesTable({
  participants,
  loading,
  page,
  total,
  pageSize,
  setPage,
  setPageSize,
  permissionUpdate,
  permissionDelete,
  handleView,
  handleEdit,
  handleViewCallHistory,
  canAddCall,
  getCallLabel,
  handleAddCall,
  getRealStatus,
  statusColors,
  handleDelete,
  getNextCallNumber,
  hasActiveFilters,
  handleReproceso,
  isAdmin,
  isRol2
}) {



  const columns = [
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          {/* Nombre del Participante */}
          <span style={{ fontWeight: 'bold', display: 'block' }}>
            {`${r.nombres} ${r.apellidos}`}
          </span>

          {/* Etiqueta del Promotor */}
          {r.promotor_asignado === "Sin asignar" ? (
            <Tag color="default" style={{ fontSize: '10px', marginTop: '4px' }}>
              Sin asignar
            </Tag>
          ) : (
            <Tag color="blue" style={{ fontSize: '10px', marginTop: '4px' }}>
              {`${r.promotor_asignado}`}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Identificación",
      dataIndex: "identificacion",
      key: "identificacion",
      width: 120,
      fixed: 'left',
    },
    {
      title: "Género",
      dataIndex: "sexo",
      align: "center",
      key: "sexo",
      width: 120,
      render: (sexo) => (sexo === "M" ? "Masculino" : "Femenino"),
    },
    {
      title: "Departamento",
      dataIndex: "departamento",
      key: "departamento",
      width: 120,
    },
    {
      title: "Municipio",
      dataIndex: "municipio",
      key: "municipio",
      width: 120,
    },
    {
      title: "Teléfono",
      dataIndex: "telefono",
      key: "telefono",
      width: 120,
    },
    {
      title: "Estado",
      key: "progreso",
      align: "center",
      width: 170,
      render: (_, record) => {
        const status = getRealStatus(record);
        const color = statusColors[status];
        const label =
          status === "En Proceso"
            ? `Intentos: ${record.total_llamadas}/3`
            : status;

        return (
          <Tag
            color={color}
            style={{
              fontSize: 13,
              width: 115,
              padding: "3px 7px",
              borderRadius: 6,
            }}
          >
            <FaPhoneAlt style={{ marginRight: 6 }} />
            {label}
          </Tag>
        );
      },
    },
    {
      title: "Acciones",
      key: "acciones",
      align: "center",
      width: 230,
      render: (_, record) => {
        const status = getRealStatus(record);
        const normalized = status
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .trim();
        const isBlocked =
          normalized === "verificado" || normalized === "no continua";
        return (
          <Row gutter={8} justify="center" className="participantes-actions">
            <Col>
              <Tooltip title="Ver detalles">
                <Button
                  type="text"
                  size="small"
                  className="action-btn"
                  icon={<FaRegEye />}
                  onClick={() => handleView(record)}
                />
              </Tooltip>
            </Col>
            {permissionUpdate && (
              <Col>
                <Tooltip title="Editar participante">
                  <Button
                    type="text"
                    size="small"
                    className="action-btn"
                    icon={<FaRegPenToSquare />}
                    onClick={() => handleEdit(record)}
                  />
                </Tooltip>
              </Col>
            )}
            <Col>
              <Tooltip
                title={
                  record.total_llamadas > 0
                    ? "Ver historial de llamadas"
                    : "Sin registros de llamadas"
                }
              >
                <Button
                  type="text"
                  size="small"
                  className={`action-btn ${record.total_llamadas === 0 ? "history-disabled" : ""
                    }`}
                  icon={<FaClockRotateLeft />}
                  disabled={record.total_llamadas === 0}
                  onClick={() => handleViewCallHistory(record)}
                />
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title={getCallLabel(record)}>
                <Button
                  type="text"
                  size="small"
                  className={`action-btn ${isBlocked ? "call-disabled" : "call-icon"
                    }`}
                  icon={<FaPhoneAlt />}
                  disabled={isBlocked || !canAddCall(record)}
                  onClick={() => handleAddCall(record)}
                />
              </Tooltip>
            </Col>
            {permissionUpdate && (isAdmin || isRol2) && (
              <Col>
                <Popconfirm
                  title="¿Reprocesar participante?"
                  description="Se eliminará todo el historial de llamadas. Esta acción no se puede deshacer."
                  okText="Sí, reprocesar"
                  cancelText="Cancelar"
                  okType="danger"
                  onConfirm={() => handleReproceso(record)}
                  disabled={record.total_llamadas === 0}
                >
                  <Tooltip title="Reprocesar participante">
                    <Button
                      type="text"
                      size="small"
                      className="action-btn action-btn-warning"
                      icon={<FaArrowsRotate />}
                      disabled={record.total_llamadas === 0}
                    />
                  </Tooltip>
                </Popconfirm>
              </Col>
            )}
            {permissionDelete && (
              <Col>
                <Popconfirm
                  title="¿Eliminar participante?"
                  description="Este participante será marcado como eliminado."
                  okText="Sí, eliminar"
                  cancelText="Cancelar"
                  onConfirm={() => handleDelete(record.id)}
                >
                  <Button
                    type="text"
                    size="small"
                    className="action-btn delete-icon action-btn-danger"
                    icon={<FaTrash />}
                  />
                </Popconfirm>
              </Col>
            )}
          </Row>
        );
      },
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={participants}
      loading={loading}
      rowKey="id"
      bordered
      size="small"
      sticky
      scroll={{ x: "max-content" }}
      pagination={{
        current: page,
        total,
        pageSize,
        showSizeChanger: true, // ✅ habilita selector
        pageSizeOptions: ['30', '50', '100', '150', '200', '500'],
        onChange: (newPage, newPageSize) => {
          setPage(newPage);
          if (newPageSize !== pageSize) {
            setPageSize(newPageSize);
            setPage(1); // 🔑 reinicia a página 1
          }
        }
      }
      }
      style={{
        borderRadius: 12,
        background: "#fff",
        marginTop: 20,
      }}
    />
  );
}
